import { useEffect, useState } from "react";
import { fetchACHDData, downloadCSVFile, updateUTRNumber } from "../Api/PaymentFileScreen";
import { Container, Table, Button, Row, Col, Form, Modal } from "react-bootstrap";

import { toast } from "react-toastify";
import moment from "moment";
import { FaEdit } from "react-icons/fa";

const PaymentFileScreen = () => {
  const [achdData, setAchdData] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showUTRModal, setShowUTRModal] = useState(false);
  const [currentUTR, setCurrentUTR] = useState("");
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  const [allSelected, setAllSelected] = useState(true);

  useEffect(() => {
    const loadACHDData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchACHDData();
        const sortedData = [...(data || [])].sort((a, b) => 
          new Date(b.createdOn) - new Date(a.createdOn)
        );
        setAchdData(sortedData);
        
        const initialCheckedState = {};
        sortedData.forEach((_, index) => {
          initialCheckedState[index] = true;
        });
        setCheckedItems(initialCheckedState);
        setAllSelected(true);
      } catch (error) {
        toast.error(`Error Fetching ACHD Data: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    loadACHDData();
  }, []);

  const handleCheckboxChange = (index) => {
    setCheckedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const toggleSelectAll = () => {
    const newCheckedState = {};
    const newSelectAllState = !allSelected;
    
    achdData.forEach((_, index) => {
      newCheckedState[index] = newSelectAllState;
    });
    
    setCheckedItems(newCheckedState);
    setAllSelected(newSelectAllState);
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const selectedItems = achdData.filter((_, index) => checkedItems[index]);
      
      if (selectedItems.length === 0) {
        toast.warning("Please select at least one item to download");
        return;
      }

      const payload = selectedItems.map(item => ({
        payrollIdgenerated: item.refNo || " "
      }));

      console.log("Sending payload to backend:", payload);

      const response = await downloadCSVFile(payload);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payment_files_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }, 100);
      
      toast.success("CSV file downloaded successfully!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error(`Error downloading CSV: ${error.response?.data?.message || error.message || "Please try again"}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleEditUTR = (index) => {
    setCurrentItemIndex(index);
    setCurrentUTR(achdData[index].utrNumber || "");
    setShowUTRModal(true);
  };

  const handleUTRSubmit = async () => {
    if (currentItemIndex !== null) {
      try {
        const currentItem = achdData[currentItemIndex];
        const payload = {
          docNo: currentItem.docno,
          enteredBy: currentItem.enteredBy,
          utrNumber: currentUTR
        };

        await updateUTRNumber(payload);

        const updatedData = [...achdData];
        updatedData[currentItemIndex].utrNumber = currentUTR;
        setAchdData(updatedData);
        setShowUTRModal(false);
        toast.success("UTR Number updated successfully!");
      } catch (error) {
        console.error("Error updating UTR:", error);
        toast.error(`Error updating UTR: ${error.response?.data?.message || error.message || "Please try again"}`);
      }
    }
  };

  return (
    
      <div className="main-content expand">
        <Container fluid className="list-container">
          <Row className="align-items-center mb-3">
            <Col>
              <span className="list-title">Payment File Screen</span>
            </Col>
            <Col xs="auto" className="d-flex">
              <Button 
                variant="outline-primary" 
                onClick={toggleSelectAll}
                className="me-2"
              >
                {allSelected ? "Unselect All" : "Select All"}
              </Button>
              <Button 
                variant="primary" 
                onClick={handleDownload}
                className="generate-btn"
                disabled={isDownloading || achdData.length === 0}
              >
                {isDownloading ? "Downloading..." : "Download CSV"}
              </Button>
            </Col>
          </Row>

          {isLoading ? (
            <p>Loading data...</p>
          ) : achdData.length > 0 ? (
            <div className="table-wrapper">
              <div className="table-scroll-container">
                <Table bordered hover className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>ID</th>
                      <th>Doc No</th>
                      <th>Doc FY</th>
                      <th>Doc FM</th>
                      <th>Doc CC</th>
                      <th>Posting Date</th>
                      <th>Document Date</th>
                      <th>Ref No</th>
                      <th>Reporting WBS</th>
                      <th>Created On</th>
                      <th>Entered By</th>
                      <th>Document Type</th>
                      <th>Approval Status</th>
                      <th>UTR Number</th> 
                      <th>Enter UTR No.</th>
                      <th>UTR Updated By</th>
                      <th>UTR Updated On</th>
                      <th>Check List</th>
                    </tr>
                  </thead>
                  <tbody>
                    {achdData.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{item.id}</td>
                        <td>{item.docno}</td>
                        <td>{item.docfy}</td>
                        <td>{item.docfm}</td>
                        <td>{item.doccc}</td>
                        <td>{moment(item.postingDate).format('L')}</td>
                        <td>{moment(item.documentDate).format('L')}</td>
                        <td>{item.refNo}</td>
                        <td>{item.reportingWbs}</td>
                        <td>{moment(item.createdOn).format('L')} at {moment(item.createdOn).format('LT')}</td>
                        <td>{item.enteredBy}</td>
                        <td>{item.documentType}</td>
                        <td>{item.approvalStatus}</td>
                        <td>{item.utrNumber}</td>
                        <td>
                          <Button 
                            variant="link" 
                            onClick={() => handleEditUTR(index)}
                            className="p-0"
                          >
                            <FaEdit className="edit-icon" />
                          </Button>
                        </td>
                        <td>{item.utrUpdatedBy}</td>
                        <td>{item.utrUpdatedOn ? moment(item.utrUpdatedOn).format('L LT') : ''}</td>
                        <td>
                          <Form.Check 
                            type="checkbox"
                            checked={checkedItems[index] || false}
                            onChange={() => handleCheckboxChange(index)}
                            className="custom-checkbox"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          ) : (
            <p className="no-data">No payment file data found.</p>
          )}
        </Container>

        {/* UTR Modal */}
        <Modal show={showUTRModal} onHide={() => setShowUTRModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Enter UTR Number</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group controlId="formUTRNumber">
              <Form.Label>UTR Number</Form.Label>
              <Form.Control
                type="text"
                value={currentUTR}
                onChange={(e) => setCurrentUTR(e.target.value)}
                placeholder="Enter UTR Number"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUTRModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleUTRSubmit}>
              Submit
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    
  );
};

export default PaymentFileScreen;